import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { customerService, CreateCustomerRequest } from '../../../../services/customerService';
import { companySettingsService } from '../../../../services/companySettingsService';
import { customerAttributeService, CustomerAttributeFieldDTO } from '../../../../services/customerAttributeService';
import CustomerAttributeFields from '../../../../components/CustomerAttributeFields';
import { useAppAccess } from '../../../../context/AppAccessContext';
import { generateCustomerCode } from '../../../../utils/codeGenerator';
import { parseApiError } from '../../../../utils/errorUtils';
import '../../../../styles/ImsTheme.css';
import '../../../../pages/Products/EntityCreatePage.css';

const MAX_LENGTHS = {
  code: 20,
  name: 150,
  contactEmail: 150,
  contactPhone: 100,
  billingAddress: 500,
  shippingAddress: 500,
  city: 100,
  state: 100,
  postalCode: 50,
  country: 100,
  taxId: 50,
};

interface FieldErrors {
  code?: string;
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  billingAddress?: string;
  shippingAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  taxId?: string;
  creditLimit?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CustomerCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentApp } = useAppAccess();
  const isCarRental = currentApp === 'carRental';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [enableAutoCustomerCode, setEnableAutoCustomerCode] = useState(true);
  const [loading, setLoading] = useState(true);

  const [attrFields, setAttrFields] = useState<CustomerAttributeFieldDTO[]>([]);
  const [attrSetName, setAttrSetName] = useState('');
  const [attrValues, setAttrValues] = useState<Record<number, string>>({});
  const [attrErrors, setAttrErrors] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState<CreateCustomerRequest>({
    code: '',
    name: '',
    contactEmail: '',
    contactPhone: '',
    billingAddress: '',
    shippingAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    creditLimit: undefined,
    paymentTerms: '',
    customerType: '',
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await companySettingsService.getCompanySettings();
        const settings = response.data?.data;
        const autoGenerate = settings?.enableAutoCustomerCode ?? true;
        setEnableAutoCustomerCode(autoGenerate);
        if (autoGenerate) {
          setFormData((prev) => ({ ...prev, code: generateCustomerCode() }));
        }
      } catch {
        setEnableAutoCustomerCode(true);
        setFormData((prev) => ({ ...prev, code: generateCustomerCode() }));
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (!isCarRental) return;
    customerAttributeService.getForm('carRental')
      .then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setAttrFields(d?.attributes ?? []);
        setAttrSetName(d?.setName ?? t('customer.driverDetails', 'Driver Details'));
      })
      .catch(() => {});
  }, [isCarRental, t]);

  const handleAttrChange = (attributeDefinitionId: number, value: string) => {
    setAttrValues(prev => ({ ...prev, [attributeDefinitionId]: value }));
    setAttrErrors(prev => ({ ...prev, [attributeDefinitionId]: '' }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'code' && enableAutoCustomerCode) {
      return;
    }

    if (name === 'creditLimit') {
      const parsed = parseFloat(value);
      setFormData((prev) => ({ ...prev, creditLimit: isNaN(parsed) ? undefined : parsed }));
      setFieldErrors((prev) => ({ ...prev, creditLimit: undefined }));
      return;
    }

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Code validation — only if not auto-generating
    if (!enableAutoCustomerCode) {
      if (!formData.code.trim()) {
        errors.code = t('customer.formValidation.codeRequired');
        isValid = false;
      } else if (formData.code.length < 3) {
        errors.code = t('customer.formValidation.codeMinLength');
        isValid = false;
      }
    }

    // Name — required + max length
    if (!formData.name.trim()) {
      errors.name = t('customer.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('customer.formValidation.nameMaxLength');
      isValid = false;
    }

    // Contact email — optional; validate format + max length only when provided
    if (formData.contactEmail.trim()) {
      if (!EMAIL_REGEX.test(formData.contactEmail)) {
        errors.contactEmail = t('customer.formValidation.emailInvalid');
        isValid = false;
      } else if (formData.contactEmail.length > MAX_LENGTHS.contactEmail) {
        errors.contactEmail = t('customer.formValidation.emailMaxLength');
        isValid = false;
      }
    }

    // Contact phone — optional, max length only
    if (formData.contactPhone && formData.contactPhone.length > MAX_LENGTHS.contactPhone) {
      errors.contactPhone = t('customer.formValidation.contactPhoneMaxLength');
      isValid = false;
    }

    // Billing address — optional, max length only
    if (formData.billingAddress && formData.billingAddress.length > MAX_LENGTHS.billingAddress) {
      errors.billingAddress = t('customer.formValidation.billingAddressMaxLength');
      isValid = false;
    }

    // Shipping address — optional, max length only
    if (formData.shippingAddress && formData.shippingAddress.length > MAX_LENGTHS.shippingAddress) {
      errors.shippingAddress = t('customer.formValidation.shippingAddressMaxLength');
      isValid = false;
    }

    // City — optional, max length only
    if (formData.city && formData.city.length > MAX_LENGTHS.city) {
      errors.city = t('customer.formValidation.cityMaxLength');
      isValid = false;
    }

    // State — optional, max length only
    if (formData.state && formData.state.length > MAX_LENGTHS.state) {
      errors.state = t('customer.formValidation.stateMaxLength');
      isValid = false;
    }

    // Postal code — optional, max length only
    if (formData.postalCode && formData.postalCode.length > MAX_LENGTHS.postalCode) {
      errors.postalCode = t('customer.formValidation.postalCodeMaxLength');
      isValid = false;
    }

    // Country — optional, max length only
    if (formData.country && formData.country.length > MAX_LENGTHS.country) {
      errors.country = t('customer.formValidation.countryMaxLength');
      isValid = false;
    }

    // Tax ID — optional, max length only
    if (formData.taxId && formData.taxId.length > MAX_LENGTHS.taxId) {
      errors.taxId = t('customer.formValidation.taxIdMaxLength');
      isValid = false;
    }

    // Credit limit — optional, must be >= 0
    if (formData.creditLimit !== undefined && formData.creditLimit !== null && formData.creditLimit < 0) {
      errors.creditLimit = t('customer.formValidation.creditLimitNegative');
      isValid = false;
    }

    setFieldErrors(errors);

    // Car Rental driver-detail attributes — required only in this app context.
    const attrErrs: Record<number, string> = {};
    if (isCarRental) {
      for (const f of attrFields) {
        if (f.isRequired && !(attrValues[f.attributeDefinitionId] ?? '').trim()) {
          attrErrs[f.attributeDefinitionId] = t('customer.formValidation.attributeRequired', '{{name}} is required', { name: f.name });
          isValid = false;
        }
      }
    }
    setAttrErrors(attrErrs);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e) ?? Object.values(attrErrs).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (!enableAutoCustomerCode) {
        try {
          const response = await customerService.checkCustomerCodeExists(formData.code);
          if (response.data?.data?.exists) {
            setFieldErrors({ code: t('customer.formValidation.codeDuplicate') });
            setError(t('customer.formValidation.codeDuplicate'));
            setSaving(false);
            return;
          }
        } catch {
          // Continue with submission
        }
      }
      const res = await customerService.createCustomer(formData);
      const created = (res.data as any)?.data ?? res.data;

      if (isCarRental && created?.id && attrFields.length > 0) {
        await customerAttributeService.save(
          created.id,
          'carRental',
          attrFields.map(f => ({ attributeDefinitionId: f.attributeDefinitionId, value: attrValues[f.attributeDefinitionId] ?? null })),
        );
      }

      navigate('/customers', { state: { message: t('customer.customerCreatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('customer.failedToCreateCustomer')).message);
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/customers')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('customer.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/customers')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="create-content">
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('customer.customerInfo')}</h2>
              </div>
              <div className="create-card-body">
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                  </div>
                ) : (
                  <form id="page-form" onSubmit={handleSubmit} noValidate>

                    {/* Customer Code */}
                    <div className="form-group">
                      <label className="form-label">
                        {t('customer.customerCode')}
                        {enableAutoCustomerCode && <span style={{ marginLeft: '8px', fontSize: '0.875rem', color: '#6c757d' }}>Auto</span>}
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        disabled={enableAutoCustomerCode}
                        placeholder={enableAutoCustomerCode ? t('customer.autoGeneratedCodePlaceholder') : t('customer.enterCustomerCode')}
                      />
                      {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                    </div>

                    {/* Customer Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('customer.customerName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.name}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">{t('customer.nameHint')}</small>}
                  </div>

                  {/* Contact Email */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('customer.contactEmail')}
                      <span className="char-counter">{formData.contactEmail.length}/{MAX_LENGTHS.contactEmail}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactEmail ? 'is-invalid' : ''}`}
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactEmail}
                    />
                    {fieldErrors.contactEmail && <small className="form-error">{fieldErrors.contactEmail}</small>}
                    {!fieldErrors.contactEmail && <small className="form-helper">{t('customer.contactEmailHint')}</small>}
                  </div>

                  {/* Contact Phone */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.contactPhone')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactPhone ? 'is-invalid' : ''}`}
                      name="contactPhone"
                      value={formData.contactPhone || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactPhone}
                    />
                    {fieldErrors.contactPhone && <small className="form-error">{fieldErrors.contactPhone}</small>}
                    {!fieldErrors.contactPhone && <small className="form-helper">{t('customer.contactPhoneHint')}</small>}
                  </div>

                  {/* Customer Type */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.customerType')}</label>
                    <select
                      className="form-select"
                      name="customerType"
                      value={formData.customerType || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('customer.selectCustomerType')}</option>
                      <option value="Retail">{t('customer.typeRetail')}</option>
                      <option value="Wholesale">{t('customer.typeWholesale')}</option>
                      <option value="Distribution">{t('customer.typeDistribution')}</option>
                      <option value="Government">{t('customer.typeGovernment')}</option>
                      <option value="Corporate">{t('customer.typeCorporate')}</option>
                      <option value="Other">{t('customer.typeOther')}</option>
                    </select>
                  </div>

                  {/* Billing Address */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.billingAddress')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.billingAddress ? 'is-invalid' : ''}`}
                      name="billingAddress"
                      value={formData.billingAddress || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.billingAddress}
                    />
                    {fieldErrors.billingAddress && <small className="form-error">{fieldErrors.billingAddress}</small>}
                    {!fieldErrors.billingAddress && <small className="form-helper">{t('customer.billingAddressHint')}</small>}
                  </div>

                  {/* Shipping Address */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.shippingAddress')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.shippingAddress ? 'is-invalid' : ''}`}
                      name="shippingAddress"
                      value={formData.shippingAddress || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.shippingAddress}
                    />
                    {fieldErrors.shippingAddress && <small className="form-error">{fieldErrors.shippingAddress}</small>}
                    {!fieldErrors.shippingAddress && <small className="form-helper">{t('customer.shippingAddressHint')}</small>}
                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.city')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.city ? 'is-invalid' : ''}`}
                      name="city"
                      value={formData.city || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.city}
                    />
                    {fieldErrors.city && <small className="form-error">{fieldErrors.city}</small>}
                    {!fieldErrors.city && <small className="form-helper">{t('customer.cityHint')}</small>}
                  </div>

                  {/* State / Province */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.state')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.state ? 'is-invalid' : ''}`}
                      name="state"
                      value={formData.state || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.state}
                    />
                    {fieldErrors.state && <small className="form-error">{fieldErrors.state}</small>}
                    {!fieldErrors.state && <small className="form-helper">{t('customer.stateHint')}</small>}
                  </div>

                  {/* Postal Code */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.postalCode')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.postalCode ? 'is-invalid' : ''}`}
                      name="postalCode"
                      value={formData.postalCode || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.postalCode}
                    />
                    {fieldErrors.postalCode && <small className="form-error">{fieldErrors.postalCode}</small>}
                    {!fieldErrors.postalCode && <small className="form-helper">{t('customer.postalCodeHint')}</small>}
                  </div>

                  {/* Country */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.country')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.country ? 'is-invalid' : ''}`}
                      name="country"
                      value={formData.country || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.country}
                    />
                    {fieldErrors.country && <small className="form-error">{fieldErrors.country}</small>}
                    {!fieldErrors.country && <small className="form-helper">{t('customer.countryHint')}</small>}
                  </div>

                  {/* Tax ID */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.taxId')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.taxId ? 'is-invalid' : ''}`}
                      name="taxId"
                      value={formData.taxId || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.taxId}
                    />
                    {fieldErrors.taxId && <small className="form-error">{fieldErrors.taxId}</small>}
                    {!fieldErrors.taxId && <small className="form-helper">{t('customer.taxIdHint')}</small>}
                  </div>

                  {/* Credit Limit */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.creditLimit')}</label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.creditLimit ? 'is-invalid' : ''}`}
                      name="creditLimit"
                      value={formData.creditLimit ?? ''}
                      onChange={handleInputChange}
                      step="0.01"
                    />
                    {fieldErrors.creditLimit && <small className="form-error">{fieldErrors.creditLimit}</small>}
                    {!fieldErrors.creditLimit && <small className="form-helper">{t('customer.creditLimitHint')}</small>}
                  </div>

                  {/* Payment Terms */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.paymentTerms')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="paymentTerms"
                      placeholder={t('customer.paymentTermsPlaceholder')}
                      value={formData.paymentTerms || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  </form>
                )}
              </div>
            </div>

            {isCarRental && attrFields.length > 0 && (
              <CustomerAttributeFields
                title={attrSetName}
                fields={attrFields}
                values={attrValues}
                onChange={handleAttrChange}
                errors={attrErrors}
              />
            )}
          </div>

          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('customer.requiredFields')}</h3>
              </div>
              <div className="create-card-body">
                <div className="create-help-section">
                  <ul className="create-help-list">
                    <li>{t('customer.customerName')}</li>
                    {isCarRental && attrFields.filter(f => f.isRequired).map(f => (
                      <li key={f.attributeDefinitionId}>{f.name}</li>
                    ))}
                  </ul>
                  <hr className="create-divider" />
                  <strong>{t('customer.optionalFields')}</strong>
                  <ul className="create-help-list">
                    <li>{t('customer.contactEmail')}</li>
                    <li>{t('customer.contactPhone')}</li>
                    <li>{t('customer.customerType')}</li>
                    <li>{t('customer.billingAddress')}</li>
                    <li>{t('customer.shippingAddress')}</li>
                    <li>{t('customer.city')}</li>
                    <li>{t('customer.state')}</li>
                    <li>{t('customer.postalCode')}</li>
                    <li>{t('customer.country')}</li>
                    <li>{t('customer.taxId')}</li>
                    <li>{t('customer.creditLimit')}</li>
                    <li>{t('customer.paymentTerms')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerCreatePage;
